import { memo, useCallback, useMemo, useState } from "react";
import "./AlertMessage.css";
import LinkAndCloseComponent from "./components/LinkAndCloseComponent";
import { Icon } from "../icons/Icon";
import type { IconName } from "../icons/icons";
import FormattedTranslation from "../formatted-translation/FormattedTranslation";
import { useTranslate } from "@/hooks/use-translate/useTranslate";

export interface AlertMessageProps {
  titleLabel: string;
  textLabel: string;
  contentType: "success" | "error" | "warning" | "info";
  onClose?: () => void;
  links?: string | string[] | undefined;
  width?: number | string;
  formatType?: "feedback" | "persistent";
  formattedText?: boolean;
  textData?: string[];
}

const AlertMessage = ({
  titleLabel,
  textLabel,
  contentType,
  links,
  formatType = "persistent",
  onClose = () => {},
  formattedText,
  textData = [],
}: AlertMessageProps) => {
  const translate = useTranslate();

  const [opened, setOpened] = useState(true);

  const closeMessage = useCallback(() => {
    onClose();
    setOpened(false);
  }, [setOpened, onClose]);

  const messageIcon = useMemo(() => {
    switch (contentType) {
      case "error":
        return {
          icon: "errorX",
          color: "ib-red-dark",
        };
      case "info":
        return {
          icon: "infoCircle",
          color: "ib-blue-info-darkest",
        };
      case "success":
        return {
          icon: "success",
          color: "ib-green-success-dark",
        };
      case "warning":
        return {
          icon: "warningTriangle",
          color: "ib-yellow-warning-darkest",
        };
      default:
        return {
          icon: "infoCircle",
          color: "ib-blue-info-darkest",
        };
    }
  }, [contentType]);

  return (
    <>
      {" "}
      {opened ? (
        <div
          data-testid="coloured-message"
          className={`coloured-message ${contentType}-${formatType} w-fit`}
        >
          <div className="w-full md:w-fit flex flex-row justify-between gap-x-4 items-center md:items-start">
            <div className="h-[16px] w-[16px]" data-testid="icon-svg">
              <Icon
                name={messageIcon.icon as IconName}
                className={`text-${messageIcon.color} size-4`}
              />
            </div>
            <div className="message-title md:hidden">
              {translate(titleLabel)}
            </div>
            <LinkAndCloseComponent
              contentType={contentType}
              links={links}
              onClick={closeMessage}
              mobileView
            />
          </div>
          <div className="flex flex-col gap-y-2 grow md:transform translate-y-[-4px]">
            <div className="message-title max-md:hidden">
              {translate(titleLabel)}
            </div>

            <div className="text-left font-normal text-[14px] mr-6">
              {formattedText ? (
                <FormattedTranslation
                  label={textLabel}
                  dataToInsert={textData}
                />
              ) : (
                translate(textLabel, textData)
              )}
            </div>
          </div>
          <LinkAndCloseComponent
            contentType={contentType}
            links={links}
            onClick={closeMessage}
          />
        </div>
      ) : null}
    </>
  );
};

export default memo(AlertMessage);
